import { makeAutoObservable, useEffect } from '..';
import { Session as SessionModel, InvitationsCount as Count } from '../../../../models';

class Main {
    constructor() {
        this.init({});
        makeAutoObservable(this);
    }

    init({ organization, event }) {
        this.step = 1;
        this.organization = organization;
        this.event = event;
        this.sessions = {};
        this.tree = {};
        this.selectedSessions = {};
        this.selectedRates = {};
        this.selectedWeezDateId = 0;
        this.selectedSessionsRates = null;
        this.selectedSessionsRateGroups = null;
        this.availableQuotas = [];
        this.totalQuantity = 0;
        this.selectedAttendees = 0;
        this.emailToggle = true;
        this.attendees = [{}];
        this.subject = '';
        this.message = '';
        this.language = event?.default_language;
        this.messageValidity = true;
        this.payload = { language: this.language };
        this.validityList = [];
        this.featureFlags = organization?.feature_flags.concat(event?.feature_flags);
        this.invitationsCount = {
            count_orga: 0,
            count_event: 0
        };
        this.submitting = false;
        this.isLoadingSessions = true;

        if (this.organization?.pk && this.event?.pk) {
            Count.getAll({
                organizationId: this.organization?.pk,
                eventId: this.event?.pk
            })
                .catch(() => {})
                .then(data => {
                    this.setCounter(data);
                });
        }

        if (this?.organization?.pk && this?.event?.pk && this.useSessions) {
            SessionModel.getAll({
                organizationId: this.organization.pk,
                eventId: this.event.pk
            })
                .then(data => {
                    this.loadSessions(data);
                })
                .catch(error => {
                    console.error('Error loading sessions:', error);
                })
                .finally(() => {
                    this.isLoadingSessions = false;
                });
        }
    }

    // Init

    use({ organization, event }) {
        useEffect(() => this.init({ organization, event }), [organization?.pk, event?.pk]);
    }

    get useSessions() {
        return this.event?.use_sessions;
    }

    get loading() {
        return !(this.organization?.pk && this.event?.pk);
    }

    get ready() {
        return Boolean(this.organization?.pk && this.event?.pk);
    }

    // Premium Options

    get premiumOptions() {
        return this.featureFlags?.includes('EO_INVITATION_LIGHT');
    }

    setCounter(data) {
        if (data) {
            this.invitationsCount = {
                count_orga: data.count_orga,
                count_event: data.count_event
            };
        }
    }

    get counter() {
        return this.invitationsCount;
    }

    get invitationsQuotas() {
        if (this.premiumOptions) {
            return true;
        }
        return Boolean(this.counter.count_orga + this.attendeesCount <= 1000 && this.counter.count_event + this.attendeesCount <= 500);
    }

    get maxAttendees() {
        // Max possible orders, to prevent organizers to send 500/1000 orders to Pyvar, which can each include 50 attendees
        // To prevent timeouts
        return 101;
    }

    // Steps

    setStep(step) {
        this.step = Math.max(1, step);
    }

    setNextStep(step = 1) {
        this.setStep(this.step + step);
    }

    setPreviousStep() {
        this.setStep(this.step - 1);
    }

    // Sessions

    loadSessions(data) {
        const sessions = {};
        const payload = Object.fromEntries(data.map(data => [data.uuid, new Session({ data, parent: this })]));
        Object.assign(sessions, payload);
        this.sessions = sessions;
    }

    get options() {
        return Object.values(this.sessions);
    }

    get count() {
        return this.options.length;
    }

    setSelectedSessions(sessions, optionSession) {
        this.selectedSessions = sessions;
        //reset selected rate
        this.setSelectedRates({});
        this.selectedWeezDateId = optionSession.weez_date_id;
        this.selectedSessionsRates = optionSession.object.rates?.map(r => r.rate_id) || null;
        this.selectedSessionsRateGroups = optionSession.object.rate_groups?.map(g => g.id) || null;
    }

    get hasSelectedSessions() {
        return this.selectedSessions.length > 0;
    }

    // Rates

    setTree(tree) {
        this.tree = tree;
    }

    get rateTree() {
        return this.tree;
    }

    setSelectedRates(rates) {
        this.selectedRates = rates;
    }

    get hasSelection() {
        return this.selectedRates.length > 0;
    }

    get selectedCount() {
        return this.selectedRates.length > 0 ? this.selectedRates.length : 0;
    }

    get hasSelectedAttendees() {
        return this.attendeesCount >= 1;
    }

    get attendeesCount() {
        return this.attendees.length - 1;
    }

    // Quotas

    setAvailableQuotas(quotas) {
        this.availableQuotas = quotas;
    }

    get hasQuotas() {
        if (!this.hasSelection) {
            return false;
        }
        let _quotas = this.selectedRates[0].object?.quotas_details;
        if (!_quotas) return false;
        return !Object.values(_quotas).every(item => item === null);
    }

    get remainingQuotas() {
        if (!this.hasQuotas) return Infinity;

        let _rateQuotas = Object.values(this.selectedRates[0].object.quotas_details).filter(q => Number.isInteger(q));
        return Math.min(..._rateQuotas);
    }

    get totalRemaining() {
        const remaining = this.remainingQuotas - this.totalQuantity;
        if (remaining >= 0) {
            return remaining;
        } else {
            return 0;
        }
    }

    get quotasExceded() {
        if (!this.hasQuotas) {
            return false;
        }
        return this.remainingQuotas - this.totalQuantity < 0;
    }

    resetQuantity() {
        this.totalQuantity = 0;
    }

    setQuantity(number) {
        this.totalQuantity = this.totalQuantity + number;
    }

    // Table

    setPaste(index, data) {
        if (index === this.attendeesCount) {
            this.addRow({});
        }
        this.attendees.splice(index, 1, data);
        if (this.attendees.length > this.maxAttendees) {
            this.attendees.pop();
        }
    }

    addRow(data) {
        this.attendees.push(data);
    }

    removeRow() {
        this.attendees.pop();
    }

    // Options

    setEmailToggle(bool) {
        this.emailToggle = bool;
        this.payload.send_mail = bool;
    }

    setSubject(subject) {
        this.subject = subject;
        this.payload.mail_subject = subject;
    }

    setMessage(message) {
        this.message = message;
        this.payload.mail_body = message;
    }

    setMessageValidity(bool) {
        this.messageValidity = bool;
    }

    get messageIsValid() {
        return this.messageValidity === true;
    }

    setLanguage(language) {
        this.language = language;
        this.payload.language = language;
    }

    // Payload

    setAttendees(index, data) {
        this.attendees[index] = data;
        this.setPayload();
    }

    setPayload() {
        const payload = [];
        const list = this.attendees.map(data => data);
        this.resetQuantity();

        list.forEach(attendee => {
            attendee.session_uuid = this.useSessions ? this.selectedSessions : null;

            attendee.quantity = parseInt(attendee.quantity);

            if (attendee.quantity > 0) {
                this.setQuantity(attendee.quantity);
            }
            if (attendee !== undefined && attendee.quantity) {
                attendee.attendees = [];
                for (let i = 0; i < attendee.quantity; i++) {
                    attendee.attendees[i] = new Attendee({ parent: this, data: attendee });
                }
                payload.push(attendee);
            }
        });

        this.payload = new Payload({
            data: { orders: payload },
            parent: this
        });
    }

    setSubmit(bool) {
        this.submitting = bool;
    }

    get parsing() {
        return this.submitting;
    }
}

export const main = new Main();

export class Session {
    constructor({ data }) {
        this.text = data.start_date;
        this.value = data.uuid;
        this.weez_date_id = data.weez_date_id;
        this.object = data;
    }
}

export class Rate {
    constructor({ data }) {
        this.text = data.name;
        this.value = data.id;
    }
}
export class Payload {
    constructor({ parent, data }) {
        this.send_mail = parent.emailToggle;
        this.mail_subject = parent.subject;
        this.mail_body = parent.message;
        this.language = parent.language;
        this.orders = data.orders;
    }
}
export class Attendee {
    constructor({ parent, data }) {
        this.rate_id = parent.selectedRates[0]?.original_object.id;
        this.event_id = parent.event?.id;
        if (data.session_uuid) {
            this.session_uuid = data.session_uuid;
        }
        this.paid_price = 0;
        if (data.firstname) {
            this.first_name = data.firstname;
        }
        if (data.lastname) {
            this.last_name = data.lastname;
        }
        if (data.email) {
            this.email = data.email;
        }
    }
}
